import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { subsFor, type Account } from "@/lib/finance";
import { Pick } from "@/components/movements/TransactionFormDialog";
import { useInlineAccount } from "@/components/accounts/useInlineAccount";

export interface NewLoan { id: string; name: string; monthly: number }
interface LinkResult { name: string; category_name: string; schedule_id: string | null; next_due_date: string | null }

export function LoanBudgetPrompt({ loan, debits: debitsProp, onClose, onSaved }: { loan: NewLoan | null; debits: Account[]; onClose: () => void; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("");
  const { all: debits, create, dialog } = useInlineAccount(debitsProp);
  useEffect(() => { setSource(debitsProp.length === 1 ? debitsProp[0].id : ""); }, [loan, debitsProp]);
  const yes = async () => {
    if (!loan) return;
    if (debits.length > 0 && !source) return toast.error("Elige desde qué cuenta lo pagas");
    setBusy(true);
    try {
      const { data } = await api.post<LinkResult>(`/accounts/${loan.id}/budget-link`, { source_account_id: source || null });
      toast.success(`"${data.name}" agregado a ${data.category_name} como Deuda${data.schedule_id ? " y a Programados" : ""}`);
      if (!data.schedule_id) toast.warning("Agrega una cuenta de débito para programar este pago");
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!loan} onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="loan-budget-prompt">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1B2A4A]">¿Agregamos la mensualidad de {money(loan?.monthly ?? 0)} a tu presupuesto y a Programados?</DialogTitle>
          <DialogDescription>Se crea el concepto ligado a {loan?.name}, tipo Deuda, y su pago mensual en el día de pago.</DialogDescription>
        </DialogHeader>
        <Pick label="¿Desde qué cuenta lo pagas?" value={source} onChange={setSource} testId="loan-budget-source" placeholder="Elige tu cuenta de débito"
          options={debits.map((a) => ({ value: a.id, label: a.name }))} onCreate={() => create(subsFor(["debit"]), (a) => setSource(a.id))} />
        {debits.length === 0 && <p className="rounded-lg bg-[#FBEAE5] px-3 py-2 text-sm text-[#8E2E18]" data-testid="loan-budget-no-debit">No tienes cuentas de débito: créala aquí para programar el pago, o se crea solo el concepto.</p>}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onClose} className="h-11" data-testid="loan-budget-no">Ahora no</Button>
          <Button onClick={yes} disabled={busy} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="loan-budget-yes">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, agregar"}
          </Button>
        </div>
      </DialogContent>
      {dialog}
    </Dialog>
  );
}
